import { useMemo } from 'react';
import { CostChannel } from '@sg/graphql-schema';
import { LARGE_PRODUCT_SIZE_THRESHOLD } from '@sg/shared-fe/features/menu';

import { useDynamicFeatureFlags } from '@order/LaunchDarkly';

import {
  type CategoryUpsellsQuery,
  useCategoryUpsellsQuery,
} from '../../screens/BagScreen/GraphQL/CategoryUpsells.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useUpsellsV2 = (params: UseUpsellsParams) => {
  const { restaurantId, productIdsInCart } = params;

  const { numberOfUpsellProducts } = useDynamicFeatureFlags();

  // ─── Upsells Query ────────────────────────────────────────────────────────

  const [response] = useCategoryUpsellsQuery({
    variables: {
      restaurantId,
      costChannel: CostChannel.DefaultCostChannel,
    },
    pause: !restaurantId,
    requestPolicy: 'cache-and-network',
  });

  // ────────────────────────────────────────────────────────────

  const upsellProducts = useMemo(() => {
    if (!response.data?.categoryUpsells) {
      return [];
    }

    const firstCategory = response.data.categoryUpsells.find(
      (category) => category.categoryRanking === 1,
    );

    if (!firstCategory) {
      return [];
    }

    const productIdsInCartSet = new Set(productIdsInCart);

    const filteredPopularItems = filterUnwantedProduct(
      firstCategory.products,
      productIdsInCartSet,
    );

    return filteredPopularItems.slice(0, numberOfUpsellProducts);
  }, [
    numberOfUpsellProducts,
    productIdsInCart,
    response.data?.categoryUpsells,
  ]);

  // ─── Flags ────────────────────────────────────────────────────────────────

  const isLoadingUpsells = response.fetching;
  const maxNumberOfUpsellProducts =
    upsellProducts.length > LARGE_PRODUCT_SIZE_THRESHOLD
      ? numberOfUpsellProducts
      : LARGE_PRODUCT_SIZE_THRESHOLD;

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    upsellProducts,
    isLoadingUpsells,
    maxNumberOfUpsellProducts,
    productSize:
      upsellProducts.length <= LARGE_PRODUCT_SIZE_THRESHOLD
        ? PRODUCT_SIZES.LARGE
        : PRODUCT_SIZES.MEDIUM,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function filterUnwantedProduct(
  upsellProducts: Readonly<UpsellCategoryProduct[]>,
  existingLineItemsProductsIds: Set<string>,
) {
  return upsellProducts.filter(
    (product) =>
      !product.outOfStock && !existingLineItemsProductsIds.has(product.id),
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const PRODUCT_SIZES = {
  LARGE: 'lg',
  MEDIUM: 'md',
} as const;

// ─── Types ──────────────────────────────────────────────────────────────────

type UseUpsellsParams = {
  restaurantId: string;
  productIdsInCart: string[];
};

type UpsellCategoryProduct = UpsellCategoryObject['products'][number];

type UpsellCategoryObject = NonNullable<
  CategoryUpsellsQuery['categoryUpsells']
>[number];
